import { Button } from '@/components/ui/button';
import { AutoFilled } from '@/lib/pages/forms/components';
import { formatDate, toDateString } from '@/lib/utils/dates';
import type { Policy } from '../../../heyapi';
import {
  glacierPolicyApiCreateMidtermTransactionMutation,
  glacierPolicyApiGetPolicyQueryKey,
} from '@/lib/heyapi/@tanstack/react-query.gen';

import { TransactionType } from '@/lib/heyapi/types.gen';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/use-toast';
import { useParams } from 'react-router';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';

export function ReinstateForm({ policy }: { policy: Policy | undefined }) {
  const { id } = useParams();

  const queryClient = useQueryClient();
  const qk = glacierPolicyApiGetPolicyQueryKey({
    path: {
      account_id: id!,
    },
  });

  const createMidterm = useMutation({
    ...glacierPolicyApiCreateMidtermTransactionMutation(),
    onError: (error) => {
      toast({
        title: 'Error',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-red-300 p-4">
            <code className="text-white">{error as string}</code>
          </pre>
        ),
      });
    },
    onSuccess(_data, _variables, _context) {
      queryClient.refetchQueries({ queryKey: qk });
      toast({
        title: 'Success',
        description: (
          <pre className="mt-2 w-[340px] rounded-md bg-slate-950 p-4">
            <code className="text-white">Creating Reinstate Transaction</code>
          </pre>
        ),
      });

      // Open the Preview Page and reload the page
      setTimeout(() => {
        window.location.href = `/policy/${id}/pricing-preview`;
      }, 500);
    },
  });

  const handleSubmit = () => {
    const newDate = new Date(newEffectiveDate);

    newDate.setUTCHours(0, 0, 0, 0);
    createMidterm.mutate({
      body: {
        effective_date: newDate,
        transaction_type: TransactionType.POLICY_REINSTATED,
      },
      path: {
        account_id: id!,
      },
    });
  };

  let policyEffectiveDate = policy?.details?.policyEffectiveDate;
  let latestTransactionEffectiveDate = policy?.latestTransaction.effectiveDate;

  // Set the minimum effective date for cancellation
  // policy?.details?.policyEffectiveDate OR policy?.latestTransaction.effectiveDate
  // which ever is the latest
  let cancelMinEffectiveDate = policyEffectiveDate;

  if (
    latestTransactionEffectiveDate &&
    policyEffectiveDate &&
    latestTransactionEffectiveDate > policyEffectiveDate
  ) {
    cancelMinEffectiveDate = latestTransactionEffectiveDate;
  }

  // Turn the date into a string and replace the hyphens with slashes
  // to make it compatible with the Date constructor
  let cancelMinEffectiveString = '' + cancelMinEffectiveDate;
  cancelMinEffectiveString = cancelMinEffectiveString.replace(/-/g, '/');

  const newEffectiveDate = toDateString(new Date(cancelMinEffectiveString));

  const form = useForm({});

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-8"
      >
        <div>
          <AutoFilled
            label="Effective date"
            value={formatDate(newEffectiveDate)}
            stretch
          />
        </div>
        <Button type="submit">Confirm</Button>
      </form>
    </Form>
  );
}
